import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import PieChartDataBox from "./PieChartDataBox"
import { motion, AnimatePresence } from "framer-motion"
import { dayLabelsColumn, monthLabelsColumn, yearDataColumn, yearLabelsColumn } from "../../../utils/data"
import { useTranslation } from "react-i18next"
import ColumnChart from "./ColumnChart"
import { radioButtonBarItems } from "../../../utils/design-helpers"
import dayjs from "dayjs"
import {
  apiPieDataToPieModel,
  getAllTimeColumnLabels,
  getDataAveragePerSession,
  getDateOfHigestUsage,
  getDatesOnChangedResolution,
  getFromAndToTimestampsFromColumnClick,
  getInitialTimeStamps,
  getTotalDataUsed,
  Resolution,
  RESOLUTIONS,
  slicePieData,
  usageDataToColumnAllTimeShared,
  usageDataToColumnDataDayShared,
  usageDataToColumnDataMonthShared,
  usageDataToColumnDataYearShared,
} from "../../../utils/chart-helpers"
import { useSharedPlanById } from "../../../hooks/useSharedPlanById"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import StatisticSelectionChipButton from "../../molecules/StatisticSelectionChipButton"
import BarChartIcon from "../../atoms/icons/StatisticChipIcons/BarChartIcon"
import PieChartIcon from "../../atoms/icons/StatisticChipIcons/PieChartIcon"
import Checkbox from "../../atoms/inputs/Checkbox"
import DatePicker from "../../atoms/inputs/DatePicker"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import RadioButtonBar from "../../molecules/RadioButtonBar"
import MetricChip from "../../molecules/MetricChip"
import H4 from "../../atoms/typography/H4"
import ViewReportButton from "../../../reports/components/ViewReportButton"
import { dataPlanReportOptions } from "../../../reports/reports-helpers"
import { DataPlanModel } from "../../../utils/models"
import { GlobeIconLarge } from "../../atoms/icons/GlobeIcon"

export interface ChartSettings {
  toTimestamp: string
  fromTimestamp: string
  resolution: Resolution
}

interface DataPlanChartsViewProps {
  pieData?: any
  name?: string
  dataplan: DataPlanModel
  id: number
}

const Dot = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  background-color: ${(props) => (props.active ? props.theme.colors.greyDark : props.theme.colors.blue)};
  border-radius: 50%;
`

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }
  },
}
const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

const SharedDataChartView: React.FunctionComponent<DataPlanChartsViewProps> = ({ name, id, dataplan }) => {
  const { t } = useTranslation()
  const { now, fromTimestamp, toTimestamp } = getInitialTimeStamps()
  const [clickedSpan, setClickedSpan] = React.useState(false)
  const [statisticView, setStatisticView] = React.useState<"bar" | "pie" | "world">("pie")

  const [[page, direction], setPage] = React.useState([0, 0])
  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection])
  }

  //Initial view - year
  const [fromTime, setFromTime] = React.useState<any>(fromTimestamp)
  const [toTime, setToTime] = React.useState(toTimestamp)
  const [selectedRadio, setSelectedRadio] = React.useState<Resolution>(RESOLUTIONS.year)

  const { sharedPlan, error, loading } = useSharedPlanById(parseInt(id.toString()), selectedRadio, fromTime, toTime)

  const onSelectRadio = (spanId: Resolution) => {
    setSelectedRadio(spanId)
    const { from, to } = getDatesOnChangedResolution(fromTime, spanId, clickedSpan, sharedPlan?.start, sharedPlan?.end)

    //Adjust resolution based on view
    setFromTime(from)
    setToTime(to)
  }

  const sharedPlanUsages = sharedPlan?.totalUsageMetrics?.allUsages ?? []

  const pieUsages = sharedPlan?.totalUsageMetrics?.terminalPieStats ?? []
  const pieStats = apiPieDataToPieModel(pieUsages)
  const slicedPieData = slicePieData(pieStats)

  const sharedDataYear = usageDataToColumnDataYearShared(sharedPlanUsages)
  const dpDataMonth = usageDataToColumnDataMonthShared(sharedPlanUsages, fromTimestamp)
  const dpDataDay = usageDataToColumnDataDayShared(sharedPlanUsages)
  const dpDataAllTime = usageDataToColumnAllTimeShared(sharedPlanUsages, sharedPlan?.start, sharedPlan?.end)

  const [columnData, setColumnData] = React.useState(yearDataColumn)
  const [columnLabels, setColumnLabels] = React.useState(yearLabelsColumn)

  React.useMemo(() => {
    if (selectedRadio === RESOLUTIONS.month) {
      setColumnData(dpDataMonth ?? [])
      setColumnLabels(monthLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.day) {
      setColumnData(dpDataDay ?? [])
      setColumnLabels(dayLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.allTime) {
      const labels = getAllTimeColumnLabels(sharedPlan?.start, sharedPlan?.end)
      setColumnData(dpDataAllTime)
      setColumnLabels(labels)
    } else {
      setColumnData(sharedDataYear ?? [])
      setColumnLabels(yearLabelsColumn)
    }
  }, [sharedPlan])

  if (error) {
    console.log(`Error fetching shared plan data for dataplan ${id}`, error)
  }

  const onClickColumnInChart = (fromDate: any) => {
    if (selectedRadio === RESOLUTIONS.year) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.month)
    } else if (selectedRadio === RESOLUTIONS.month) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.day)
    } else if (selectedRadio === RESOLUTIONS.allTime) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.year)
    } else {
      console.log("cannot click this column")
    }
  }

  const sessions = sharedPlanUsages.length
  const dateHighestUsed = getDateOfHigestUsage(sharedPlanUsages, selectedRadio)
  const avgSession = getDataAveragePerSession(sharedPlanUsages)
  const totalDataUsed = getTotalDataUsed(sharedPlanUsages)

  const onSelectFromDate = (value: string) => {
    setFromTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onSelectToDate = (value: string) => {
    setToTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onCheckAllTime = () => {
    setSelectedRadio(RESOLUTIONS.allTime)
    setFromTime(dayjs(sharedPlan?.start).format("YYYY-MM-DDTHH:mm:ssZ"))
    setToTime(dayjs(sharedPlan?.end).format("YYYY-MM-DDTHH:mm:ssZ"))
  }

  return (
    <Container style={{ padding: "2rem 0" }}>
      <Row justify="center" align="center">
        <Subtitle textAlign="center">
          {name} {t("statistics")}
        </Subtitle>
        <Spacer spacing={1} />
        <ViewReportButton
          small
          options={dataPlanReportOptions(dataplan, {
            fromTimestamp: fromTime,
            toTimestamp: toTime,
            resolution: selectedRadio,
          })}
        />
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" style={{ padding: 15 }}>
        <Col lg={6}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <H3>Show by: </H3>
            <Spacer spacing={1} />
            <RadioButtonBar items={radioButtonBarItems} selectedValue={selectedRadio} onClickItem={onSelectRadio} />
          </div>
          <HorizontalSpacer spacing={2} />
          <div style={{ display: "flex", alignItems: "center" }}>
            <DatePicker
              onSelectDate={onSelectFromDate}
              value={dayjs(fromTime).format("YYYY-MM-DD")}
              disabled={selectedRadio === RESOLUTIONS.allTime}
              useMinDate={false}
            />
            -
            <DatePicker
              onSelectDate={onSelectToDate}
              value={dayjs(toTime).format("YYYY-MM-DD")}
              disabled={selectedRadio === RESOLUTIONS.allTime}
              useMinDate={false}
            />
            <Hidden xs md>
              <Spacer spacing={1} />
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={selectedRadio === RESOLUTIONS.allTime} onChange={onCheckAllTime} />
                <Spacer spacing={1} /> All time
              </div>
            </Hidden>
          </div>
        </Col>
        <Hidden xs md>
          <Col md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <StatisticSelectionChipButton
              icon={<BarChartIcon />}
              title="Usage statistics"
              selected={statisticView === "bar"}
              onClick={() => setStatisticView("bar")}
            />
            <Spacer spacing={1} />
            <StatisticSelectionChipButton
              icon={<PieChartIcon />}
              title="Individual terminal statistics"
              selected={statisticView === "pie"}
              onClick={() => setStatisticView("pie")}
            />
            <Spacer spacing={1} />

            <StatisticSelectionChipButton
              icon={<GlobeIconLarge />}
              title="Location statistics"
              selected={statisticView === "world"}
              onClick={() => console.log("world")}
              disabled
            />
          </Col>
        </Hidden>
      </Row>
      <Hidden xs md>
        <HorizontalSpacer spacing={3} />
        <Row justify="around">
          <MetricChip value={t("graphs.times_used")} icon={<H4 textAlign="center">{sessions}</H4>} />
          <MetricChip value="Date of highest used" icon={<H4 textAlign="center">{dateHighestUsed}</H4>} />
          <MetricChip value="Data average per session" icon={<H4 textAlign="center">{avgSession}</H4>} />
          <MetricChip value="Total data used" icon={<H4 textAlign="center">{totalDataUsed}</H4>} />
        </Row>
      </Hidden>
      <HorizontalSpacer spacing={3} />

      <Hidden xs md>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div>
            {statisticView === "bar" ? (
              <ColumnChart
                data={columnData}
                labels={columnLabels}
                columnWidth={16}
                onClickColumnInChart={onClickColumnInChart}
              />
            ) : (
              <PieChartDataBox data={slicedPieData} />
            )}
          </div>
        )}
      </Hidden>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Visible xs md>
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x)

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1)
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1)
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  marginTop: "1rem",
                }}
              >
                <Dot active={page === 0} />
                <Spacer spacing={2} />
                <Dot active={page !== 0} />
              </div>

              {page === 0 ? (
                <>
                  <ColumnChart
                    data={columnData}
                    labels={columnLabels}
                    columnWidth={16}
                    onClickColumnInChart={onClickColumnInChart}
                    width={500}
                  />
                </>
              ) : (
                <PieChartDataBox data={slicedPieData} />
              )}
            </motion.div>
          </AnimatePresence>
        </Visible>
      )}
    </Container>
  )
}

export default SharedDataChartView
